import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { X, ClipboardList, Sun, Moon, Download, Send } from 'lucide-react';
import { useSession } from '../contexts/SessionContext';
import { ROUTES, shiftHandoverVoicePath } from '../constants/routes';
import PatientHandoverCard from '../components/shiftHandover/PatientHandoverCard';
import { generateShiftHandoverPDF } from '../services/shiftHandoverPdf';
import { toSinglePatientHandoverNote } from '../utils/handoverUtils';

export default function ShiftHandoverScreen() {
  const navigate = useNavigate();
  const {
    shiftHandoverDrafts,
    updatePatientHandoverDraft,
    clearShiftHandoverDrafts,
    saveNote,
    nurse,
  } = useSession();
  const [submitting, setSubmitting] = useState(false);
  const shiftDate = new Date().toLocaleDateString('en-JO', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const isDay = new Date().getHours() >= 7 && new Date().getHours() < 19;

  const handlePdf = () => {
    generateShiftHandoverPDF({ drafts: shiftHandoverDrafts, nurse, shiftDate, isDay });
  };

  const handleSubmit = () => {
    if (!shiftHandoverDrafts.length || submitting) return;
    setSubmitting(true);
    try {
      const timestamp = new Date().toISOString();
      const encounterGroupId = `shift-handover-${Date.now()}`;

      shiftHandoverDrafts.forEach((draft, index) => {
        const handoverNote = toSinglePatientHandoverNote(
          { datetime: draft.generatedAt || timestamp },
          {
            patient_name: draft.patientName,
            room: draft.patientRoom,
            narrative: draft.narrative,
            sbar: draft.sbar,
            outstanding_tasks: draft.outstanding_tasks,
            negations_detected: [],
            flags: draft.flags || [],
          }
        );

        saveNote({
          id: `${encounterGroupId}-${index}`,
          patient_id: draft.patientId || null,
          patient_name: draft.patientName || 'Unknown Patient',
          patient_room: draft.patientRoom || null,
          note_type: 'handover',
          datetime: draft.generatedAt || timestamp,
          encounter_group_id: encounterGroupId,
          note_content: handoverNote,
          openaiNote: handoverNote,
          transcript: '',
          transcript_raw: '',
          transcript_cleaned: '',
          pdf_saved: false,
          model_used: 'openai',
          generatedNotes: [{ type: 'handover', note: handoverNote, error: null }],
          cortiNote: null,
          cortiRaw: null,
          cortiFailed: true,
          openaiError: null,
          activeTab: 'openai',
        });
      });

      clearShiftHandoverDrafts();
      navigate(ROUTES.REPORTS);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-page pb-28">
      <header className="border-b border-gray-200 bg-surface px-4 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-lg font-bold">Shift Handover</h1>
              <p className="text-sm text-gray-500">{shiftDate}</p>
            </div>
          </div>
          <button type="button" onClick={() => navigate(ROUTES.PATIENTS)}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className={`flex items-center gap-1 rounded-pill px-2 py-1 text-xs font-semibold ${isDay ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'}`}>
            {isDay ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            {isDay ? 'Day' : 'Night'}
          </span>
          <span className="text-gray-600">
            {nurse.name} · {nurse.hospital}
          </span>
        </div>
      </header>

      <div className="px-4 py-4">
        <p className="text-section-label mb-4">Shift reports — review and edit before saving</p>

        {shiftHandoverDrafts.length === 0 ? (
          <p className="text-center text-gray-500">No patients documented this shift.</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {shiftHandoverDrafts.map((draft) => (
              <PatientHandoverCard
                key={draft.patientKey}
                draft={draft}
                onChange={(updated) => updatePatientHandoverDraft(draft.patientKey, updated)}
                onAddVoiceNote={() => navigate(shiftHandoverVoicePath(draft.patientKey))}
              />
            ))}
          </div>
        )}
      </div>

      {shiftHandoverDrafts.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <div className="mx-auto flex w-full max-w-lg gap-3">
            <button
              type="button"
              onClick={handlePdf}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-white"
              aria-label="Download PDF"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-success py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {submitting ? 'Submitting...' : 'Submit Shift Reports'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
